import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  strategicAxes: [],
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
};

function handleRequest(state: State): State {
  return { ...state, request: setRequestStateToPending() };
}

function handleSuccess(
  state: State,
  { strategicAxes }: Boundaries["retrieveStrategicAxes"]["success"],
): State {
  return { strategicAxes, request: setRequestStateToSuccess() };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

function handleCreateStrategicAxisRequest(
  state: State,
  { strategicAxis }: Boundaries["createStrategicAxis"]["request"],
): State {
  return {
    ...state,
    strategicAxes: [
      { ...strategicAxis, uuid: strategicAxis.name },
      ...state.strategicAxes,
    ],
  };
}

function handleCreateStrategicAxisSuccess(
  state: State,
  { strategicAxis }: Boundaries["createStrategicAxis"]["success"],
): State {
  return {
    ...state,
    strategicAxes: [
      strategicAxis,
      ...state.strategicAxes.filter(({ uuid }) => uuid !== strategicAxis.name),
    ],
  };
}

function handleCreateStrategicAxisFailure(
  state: State,
  { strategicAxis }: Boundaries["createStrategicAxis"]["failure"],
): State {
  return {
    ...state,
    strategicAxes: state.strategicAxes.filter(
      ({ uuid }) => uuid !== strategicAxis.name,
    ),
  };
}

function handleRemoveStrategicAxisRequest(
  state: State,
  strategicAxis: Boundaries["removeStrategicAxis"]["request"],
): State {
  return {
    ...state,
    strategicAxes: state.strategicAxes.filter(
      ({ uuid }) => uuid !== strategicAxis.uuid,
    ),
  };
}

function handleRemoveStrategicAxisFailure(
  state: State,
  strategicAxis: Boundaries["removeStrategicAxis"]["failure"],
): State {
  return {
    ...state,
    strategicAxes: [strategicAxis, ...state.strategicAxes],
  };
}

function handleEditStrategicAxisRequest(
  state: State,
  {
    strategicAxis,
    nextStrategicAxis,
  }: Boundaries["editStrategicAxis"]["request"],
): State {
  return {
    ...state,
    strategicAxes: state.strategicAxes.map((p) =>
      p.uuid === strategicAxis.uuid
        ? { ...strategicAxis, ...nextStrategicAxis }
        : p,
    ),
  };
}

function handleEditStrategicAxisSuccess(
  state: State,
  { strategicAxis }: Boundaries["editStrategicAxis"]["success"],
): State {
  return {
    ...state,
    strategicAxes: state.strategicAxes.map((p) =>
      p.uuid === strategicAxis.uuid ? strategicAxis : p,
    ),
  };
}

function handleEditStrategicAxisFailure(
  state: State,
  { strategicAxis }: Boundaries["editStrategicAxis"]["failure"],
): State {
  return {
    ...state,
    strategicAxes: state.strategicAxes.map((p) =>
      p.uuid === strategicAxis.uuid ? strategicAxis : p,
    ),
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveStrategicAxesRequested]: handleRequest,
    [events.retrieveStrategicAxesSucceeded]: handleSuccess,
    [events.retrieveStrategicAxesFailed]: handleFailure,
    [events.createStrategicAxisRequested]: handleCreateStrategicAxisRequest,
    [events.createStrategicAxisSucceeded]: handleCreateStrategicAxisSuccess,
    [events.createStrategicAxisFailed]: handleCreateStrategicAxisFailure,
    [events.removeStrategicAxisRequested]: handleRemoveStrategicAxisRequest,
    [events.removeStrategicAxisFailed]: handleRemoveStrategicAxisFailure,
    [events.editStrategicAxisRequested]: handleEditStrategicAxisRequest,
    [events.editStrategicAxisSucceeded]: handleEditStrategicAxisSuccess,
    [events.editStrategicAxisFailed]: handleEditStrategicAxisFailure,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
